<template lang="pug">
.rank-graph
  canvas(ref="canvas")
</template>

<script>
const PC_MIN_WIDTH = 768
const GRAPH_LINE_COLOR = '#ff6fa3'
const GRAPH_TEXT_COLOR = '#ff8fb6'

export default {
  name: 'RankGraph',
  props: {
    values: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      ctx: null,
      width: 0,
      height: 0,
      paddingTop: 30,
      paddingBottom: 20,
      paddingLeft: 30,
      paddingRight: 30,
      pointRadius: 5,
    }
  },
  watch: {
    values() {
      this.draw()
    },
    '$i18n.locale'() {
      this.draw()
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
    this.handleResize()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    isPC() {
      return window.innerWidth >= PC_MIN_WIDTH
    },
    initCanvas() {
      const canvas = this.$refs.canvas
      this.width = canvas.parentElement ? canvas.parentElement.clientWidth : 0
      this.height = this.isPC() ? 180 : 160
      canvas.width = this.width
      canvas.height = this.height
      this.ctx = canvas.getContext('2d')
      this.ctx.lineWidth = this.isPC() ? 3 : 2
      this.pointRadius = this.isPC() ? 5 : 4
      this.ctx.textAlign = 'center'
      this.ctx.textBaseline = 'bottom'
    },
    handleResize() {
      this.initCanvas()
      this.draw()
    },
    getRankLabel(rank) {
      return this.$i18n.locale === 'en' ? `${rank}` : `${rank}位`
    },
    calcY(value, min, max) {
      const plotTop = this.paddingTop + this.pointRadius
      const plotBottom = this.height - this.paddingBottom - this.pointRadius
      if (max === min) {
        return (plotBottom + plotTop) / 2
      }
      const plotHeight = plotBottom - plotTop
      const ratio = (value - min) / (max - min)
      return plotTop + ratio * plotHeight
    },
    draw() {
      if (!this.ctx) {
        return
      }

      this.ctx.clearRect(0, 0, this.width, this.height)

      if (!this.values || this.values.length === 0) {
        return
      }

      const validValues = this.values.filter(v => v != null)
      if (validValues.length === 0) {
        return
      }

      const min = Math.min(...validValues)
      const max = Math.max(...validValues)

      const count = this.values.length
      const plotWidth = this.width - this.paddingLeft - this.paddingRight
      const stepX = count > 1 ? plotWidth / (count - 1) : 0

      let lastPoint = null
      this.values.forEach((rank, index) => {
        if (rank == null) {
          return
        }
        const x = this.paddingLeft + stepX * index
        const y = this.calcY(rank, min, max)

        if (lastPoint) {
          this.ctx.strokeStyle = GRAPH_LINE_COLOR
          this.ctx.beginPath()
          this.ctx.moveTo(lastPoint.x, lastPoint.y)
          this.ctx.lineTo(x, y)
          this.ctx.stroke()
          this.ctx.closePath()
        }

        this.ctx.fillStyle = GRAPH_LINE_COLOR
        this.ctx.beginPath()
        this.ctx.arc(x, y, this.pointRadius, 0, Math.PI * 2)
        this.ctx.fill()
        this.ctx.closePath()

        this.ctx.fillStyle = GRAPH_TEXT_COLOR
        this.ctx.font = this.isPC() ? 'bold 22px sans-serif' : 'bold 14px sans-serif'
        this.ctx.fillText(this.getRankLabel(rank), x, y - 8)

        lastPoint = { x, y }
      })
    },
  },
}
</script>

<style scoped lang="scss">
.rank-graph {
  width: 100%;

  canvas {
    width: 100%;
    height: auto;
    display: block;
  }
}
</style>
